import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Upload, FileText, Trash2, Eye, EyeOff, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const PayrollManagement = () => {
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [users, setUsers] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadMode, setUploadMode] = useState('single'); // 'single' or 'bulk'
  const [formData, setFormData] = useState({
    user_id: '',
    month: new Date().toISOString().slice(0, 7), // YYYY-MM
    year: new Date().getFullYear(),
    file_url: '',
    file_name: '',
    notes: ''
  });
  const [bulkProcessing, setBulkProcessing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [docsRes, usersRes] = await Promise.all([
        axios.get('/admin/payroll/documents'),
        axios.get('/admin/staff')
      ]);
      
      setDocuments(docsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error('Bitte nur PDF-Dateien hochladen');
        return;
      }
      
      // In production: Upload to S3/Cloud Storage
      // For now: Use mock URL
      const mockUrl = `https://example.com/payroll/${file.name}`;
      
      setFormData(prev => ({
        ...prev,
        file_url: mockUrl,
        file_name: file.name
      }));
      
      toast.success('Datei bereit zum Hochladen');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (uploadMode === 'single') {
      if (!formData.user_id || !formData.file_url) {
        toast.error('Bitte alle Pflichtfelder ausfüllen');
        return;
      }

      try {
        setLoading(true);
        await axios.post('/admin/payroll/upload', formData);
        
        toast.success('Lohnabrechnung erfolgreich hochgeladen');
        setShowUpload(false);
        setFormData({
          user_id: '',
          month: new Date().toISOString().slice(0, 7),
          year: new Date().getFullYear(),
          file_url: '',
          file_name: '',
          notes: ''
        });
        
        loadData();
      } catch (error) {
        console.error('Error uploading:', error);
        toast.error('Fehler beim Hochladen');
      } finally {
        setLoading(false);
      }
    } else {
      // Bulk processing
      handleBulkProcess();
    }
  };

  const handleBulkProcess = async () => {
    if (!formData.file_url || !formData.month) {
      toast.error('Bitte PDF und Monat auswählen');
      return;
    }

    try {
      setBulkProcessing(true);
      setLoading(true);
      
      toast.info('AI-Extraktion gestartet... Dies kann einen Moment dauern.');
      
      const response = await axios.post('/admin/payroll/bulk-process', {
        pdf_url: formData.file_url,
        month: formData.month,
        year: formData.year
      });
      
      if (response.data.success) {
        toast.success(`Erfolgreich! ${response.data.created} Abrechnungen automatisch erstellt 🎉`);
        
        if (response.data.not_found && response.data.not_found.length > 0) {
          toast.warning(`Achtung: ${response.data.not_found.length} Mitarbeiter nicht gefunden`);
        }
        
        setShowUpload(false);
        setFormData({
          user_id: '',
          month: new Date().toISOString().slice(0, 7),
          year: new Date().getFullYear(),
          file_url: '',
          file_name: '',
          notes: ''
        });
        
        loadData();
      }
    } catch (error) {
      console.error('Error bulk processing:', error);
      toast.error('Fehler bei der AI-Verarbeitung');
    } finally {
      setBulkProcessing(false);
      setLoading(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Möchten Sie diese Lohnabrechnung wirklich löschen?')) return;

    try {
      await axios.delete(`/admin/payroll/${documentId}`);
      toast.success('Lohnabrechnung gelöscht');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const groupByMonth = (docs) => {
    const grouped = {};
    docs.forEach(doc => {
      const key = doc.month;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(doc);
    });
    return grouped;
  };

  const groupedDocuments = groupByMonth(documents);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-white">Lohnabrechnungen</h1>
          <p className="text-zinc-400 mt-2">Verwalten Sie Lohnabrechnungen für Mitarbeiter</p>
        </div>
        <Button onClick={() => setShowUpload(!showUpload)} size="lg">
          {showUpload ? 'Abbrechen' : '+ Neue Abrechnung hochladen'}
        </Button>
      </div>

      {/* Upload Form */}
      {showUpload && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white">Lohnabrechnung hochladen</CardTitle>
            <CardDescription>
              {uploadMode === 'single' 
                ? 'Laden Sie eine PDF-Lohnabrechnung für einen Mitarbeiter hoch'
                : '🤖 AI-gestützte Massen-Verarbeitung: System extrahiert automatisch alle Mitarbeiterdaten'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Upload Mode Selector */}
            <div className="mb-6 flex gap-3">
              <Button
                type="button"
                variant={uploadMode === 'single' ? 'default' : 'outline'}
                onClick={() => setUploadMode('single')}
                className="flex-1"
              >
                Einzeln hochladen
              </Button>
              <Button
                type="button"
                variant={uploadMode === 'bulk' ? 'default' : 'outline'}
                onClick={() => setUploadMode('bulk')}
                className="flex-1"
              >
                🤖 AI Bulk-Upload (Steuerberater-PDF)
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {uploadMode === 'single' && (
                <div>
                  <Label htmlFor="user_id">Mitarbeiter *</Label>
                  <select
                    id="user_id"
                    value={formData.user_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, user_id: e.target.value }))}
                    className="w-full mt-1 p-2 bg-zinc-950 border border-zinc-800 rounded-md text-white"
                    required={uploadMode === 'single'}
                  >
                    <option value="">Mitarbeiter auswählen</option>
                    {users.map(user => (
                      <option key={user.user_id} value={user.user_id}>
                        {user.name} ({user.email})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {uploadMode === 'bulk' && (
                <div className="p-4 bg-blue-900/20 border border-blue-700 rounded-lg">
                  <h4 className="text-blue-400 font-semibold mb-2 flex items-center gap-2">
                    🤖 Automatische AI-Verarbeitung
                  </h4>
                  <p className="text-sm text-zinc-300">
                    Das System wird automatisch:
                  </p>
                  <ul className="text-sm text-zinc-400 mt-2 space-y-1 list-disc list-inside">
                    <li>Alle Mitarbeiternamen im PDF erkennen</li>
                    <li>Brutto- und Nettolöhne extrahieren</li>
                    <li>Jedem Mitarbeiter seine Abrechnung zuweisen</li>
                    <li>Push-Benachrichtigungen versenden</li>
                  </ul>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="month">Monat *</Label>
                  <Input
                    id="month"
                    type="month"
                    value={formData.month}
                    onChange={(e) => setFormData(prev => ({ ...prev, month: e.target.value }))}
                    className="bg-zinc-950 border-zinc-800 text-white"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="year">Jahr</Label>
                  <Input
                    id="year"
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                    className="bg-zinc-950 border-zinc-800 text-white"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="file">PDF Datei *</Label>
                <Input
                  id="file"
                  type="file"
                  accept=".pdf"
                  onChange={handleFileUpload}
                  className="bg-zinc-950 border-zinc-800 text-white"
                  required
                />
                {formData.file_name && (
                  <p className="text-sm text-green-400 mt-2">✓ {formData.file_name}</p>
                )}
              </div>

              <div>
                <Label htmlFor="notes">Notizen (optional)</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Zusätzliche Informationen..."
                  className="bg-zinc-950 border-zinc-800 text-white"
                  rows={3}
                />
              </div>

              <Button type="submit" className="w-full" size="lg" disabled={loading}>
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                    {bulkProcessing && <span>AI verarbeitet...</span>}
                  </div>
                ) : uploadMode === 'bulk' ? (
                  <>
                    🤖 AI-Verarbeitung starten
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-5 w-5" />
                    Abrechnung hochladen
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Documents List */}
      <div className="space-y-6">
        {Object.keys(groupedDocuments).length === 0 ? (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-12 text-center">
              <FileText className="h-16 w-16 text-zinc-700 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Noch keine Abrechnungen</h3>
              <p className="text-zinc-400 mb-4">Laden Sie die erste Lohnabrechnung hoch</p>
            </CardContent>
          </Card>
        ) : (
          Object.entries(groupedDocuments).sort((a, b) => b[0].localeCompare(a[0])).map(([month, docs]) => (
            <Card key={month} className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    {new Date(month + '-01').toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
                  </CardTitle>
                  <Badge className="bg-blue-900/30 text-blue-400 border-blue-700">
                    {docs.length} Abrechnung(en)
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {docs.map(doc => (
                    <div
                      key={doc.document_id}
                      className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg hover:bg-zinc-700 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <FileText className="h-8 w-8 text-blue-400" />
                        <div>
                          <p className="font-semibold text-white">{doc.user_name}</p>
                          <p className="text-sm text-zinc-400">{doc.file_name}</p>
                          {doc.notes && (
                            <p className="text-xs text-zinc-500 mt-1">{doc.notes}</p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        {doc.viewed ? (
                          <Badge className="bg-green-900/30 text-green-400 border-green-700">
                            <Eye className="h-3 w-3 mr-1" />
                            Gesehen
                          </Badge>
                        ) : (
                          <Badge className="bg-zinc-800 text-zinc-400 border-zinc-700">
                            <EyeOff className="h-3 w-3 mr-1" />
                            Ungelesen
                          </Badge>
                        )}
                        
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(doc.document_id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default PayrollManagement;
